import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SpotlightSearchService, RouteItem } from '../../services/spotlight-search.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ngx-spotlight-search',
  templateUrl: './spotlight-search.component.html',
  styleUrls: ['./spotlight-search.component.scss']
})
export class SpotlightSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;
  
  isOpen = false;
  searchQuery = '';
  filteredRoutes: RouteItem[] = [];
  selectedIndex = 0;
  
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private spotlightService: SpotlightSearchService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Subscribe to open state
    this.spotlightService.isOpen$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOpen => {
        this.isOpen = isOpen;
        if (isOpen) {
          this.searchQuery = '';
          this.selectedIndex = 0;
          this.performSearch('');
          setTimeout(() => this.focusInput(), 50);
        }
      });

    // Setup debounced search
    this.searchSubject
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(query => {
        this.performSearch(query);
      });
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.focusInput();
    }
   
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Cmd+K on Mac, Ctrl+K on Windows/Linux
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.spotlightService.toggle();
      return;
    }

    // Only handle these keys when spotlight is open
    if (!this.isOpen) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.selectNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectPrevious();
        break;
      case 'Enter':
        event.preventDefault();
        this.navigateToSelected();
        break;
    }
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.selectedIndex = 0;
    this.searchSubject.next(query);
  }

  private performSearch(query: string): void {
    this.filteredRoutes = this.spotlightService.searchRoutes(query);
  }

  selectNext(): void {
    if (this.selectedIndex < this.filteredRoutes.length - 1) {
      this.selectedIndex++;
      this.scrollToSelected();
    }
  }

  selectPrevious(): void {
    if (this.selectedIndex > 0) {
      this.selectedIndex--;
      this.scrollToSelected();
    }
  }

  selectRoute(index: number): void {
    this.selectedIndex = index;
  }

  navigateToSelected(): void {
    if (this.filteredRoutes[this.selectedIndex]) {
      this.navigateToRoute(this.filteredRoutes[this.selectedIndex]);
    }
  }

  navigateToRoute(route: RouteItem): void {
    this.spotlightService.navigateToRoute(route.link);
  }

  close(): void {
    this.spotlightService.close();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  private focusInput(): void {
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const selectedElement = document.querySelector('.spotlight-result-item.selected');
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }

  getBreadcrumbText(route: RouteItem): string {
    if (!route.breadcrumb || route.breadcrumb.length === 0) {
      return '';
    }
    // Skip the last item as it's the route title itself
    // Translate each breadcrumb item
    return route.breadcrumb
      .slice(0, -1)
      .map(crumb => this.translate.instant(crumb))
      .join(' › ');
  }

  getShortcutText(): string {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    return isMac ? '⌘K' : 'Ctrl+K';
  }
}
